<template>
	<div :class="{ 'form-input-wrap': true, 'no-margin-bottom': noMarginBottom }">
		<div :class="{ 'label-wrap': true }" v-if="showResolvedLabel && (['checkbox', 'radio'].indexOf(theType) < 0 || forceLabel)">
			<label :style="labelStyles"
				>{{ resolvedLabel }} &nbsp;&nbsp;<sup v-if="required">{{ requiredText }}</sup></label
			>
		</div>

		<div class="suffix"><slot></slot></div>

		<div :class="{ 'input-wrap-wrap': true, check: ['checkbox', 'radio'].indexOf(theType) >= 0, 'inner-error': !!innerError }">
			<HelpTip v-if="tip" position="fromRight">{{ tip }}</HelpTip>

			<span class="inner-error" v-if="innerError && ['checkbox', 'radio'].indexOf(theType) < 0" v-html="innerError" />
			<span class="input-corner-label" v-if="cornerLabel && ['checkbox', 'radio'].indexOf(theType) < 0">{{ cornerLabel }}</span>

			<v-select
				v-if="theType === 'select'"
				:class="selectClasses"
				:placeholder="placeholder"
				:name="name"
				:id="id"
				:value="currentValue"
				@input="handleVSelectInput"
				:options="processedItems"
				:reduce="(option) => option._id"
				:multiple="multiple"
				:required="required">
				<template #search="{ attributes, events }">
					<input :readonly="isMobile" class="vs__search" v-bind="attributes" v-on="events" />
				</template>
			</v-select>

			<div
				class="type-effect-shell"
				v-if="['text', 'email', 'password', 'tel', 'search'].indexOf(theType) >= 0"
				:class="{ 'type-effect-shell--animating': showTypeEffectOverlay }">
				<template v-if="textOnly">
					<div
						:class="[
							{
								'has-error': !!hasError && !emptyError && !innerError,
								required: required,
								'has-tip': tip !== '',
								'form-input-text-only': true,
							},
							classes2,
							{ 'form-input-text-only--placeholder': isTextOnlyPlaceholder },
						]"
						:aria-busy="isTypeEffectAnimating">
						{{ textOnlyDisplayValue }}
					</div>
				</template>
				<input
					v-else
					:class="[{ 'has-error': !!hasError && !emptyError && !innerError, required: required, 'has-tip': tip !== '' }, classes2]"
					:type="theType"
					:name="name"
					:id="id"
					:value="renderedValue"
					@input="
						handleTextInput($event.target.value);
						errorCheck();
					"
					@keyup="keyuped"
					@change="errorCheck"
					@blur="blurred"
					@focus="focussed"
					:placeholder="placeholder"
					:disabled="disabled"
					:readonly="isInteractionBlocked"
					:autocomplete="autocomplete"
					:aria-busy="isTypeEffectAnimating"
					@keyup.enter="emitKeyUp('enter')"
					@keyup.esc="emitKeyUp('esc')"
					:ref="theRef" />

				<span v-if="showTypeEffectOverlay" class="type-effect-overlay" aria-hidden="true"></span>
			</div>

			<input
				v-if="['number'].indexOf(theType) >= 0"
				:class="[{ 'has-error': !!hasError && !emptyError, required: required, 'has-tip': tip !== '' }, classes2]"
				:type="theType"
				:name="name"
				:id="id"
				:value="currentValue"
				@input="
					emitValue($event.target.value);
					errorCheck();
				"
				@keyup="errorCheck"
				@blur="blurred"
				@focus="focussed"
				@change="errorCheck"
				:placeholder="placeholder"
				:disabled="disabled"
				:min="min"
				:max="max"
				@keyup.enter="emitKeyUp('enter')"
				@keyup.esc="emitKeyUp('esc')"
				:ref="theRef" />

			<div
				:class="[{ 'has-error': !!hasError && !emptyError, required: required, 'date-filter-wrap': true }, classes2]"
				v-if="['date'].indexOf(theType) >= 0">
				<DateFilter
					@change="setDate"
					@blur="blurred"
					@focus="focussed"
					:value="currentValue"
					:placeholder="placeholder"
					mode="single"
					:ref="theRef" />
			</div>

			<div class="type-effect-shell" v-if="theType === 'textarea'" :class="{ 'type-effect-shell--animating': showTypeEffectOverlay }">
				<template v-if="textOnly">
					<div
						:class="[
							{
								'has-error': !!hasError && !emptyError,
								required: required,
								'has-tip': tip !== '',
								'form-input-text-only': true,
								'form-input-text-only--multiline': true,
							},
							classes2,
							{ 'form-input-text-only--placeholder': isTextOnlyPlaceholder },
						]"
						:aria-busy="isTypeEffectAnimating">
						{{ textOnlyDisplayValue }}
					</div>
				</template>
				<textarea
					v-else
					:class="[
						{ 'has-error': !!hasError && !emptyError, required: required, 'has-tip': tip !== '', 'auto-resize': autoResize },
						classes2,
					]"
					:type="theType"
					:name="name"
					:id="id"
					:value="renderedValue"
					@input="
						handleTextInput($event.target.value);
						errorCheck();
					"
					@keyup="keyuped"
					@blur="blurred"
					@focus="focussed"
					@change="errorCheck"
					:placeholder="placeholder"
					:disabled="disabled"
					:readonly="isInteractionBlocked"
					:aria-busy="isTypeEffectAnimating"
					@keyup.enter="emitKeyUp('enter')"
					@keyup.esc="emitKeyUp('esc')"
					:style="textareaStyles"
					:ref="theRef"
					:rows="autoResize && '2'" />

				<span v-if="showTypeEffectOverlay" class="type-effect-overlay" aria-hidden="true"></span>
			</div>

			<div
				:class="[{ 'checkbox-wrap': true, inline: true }, classes2]"
				v-if="['checkbox', 'radio'].indexOf(theType) >= 0 && items.length === 0 && typeof label !== 'undefined'">
				<input
					:class="[{ 'has-error': !!hasError && !emptyError, required: required }, classes2]"
					:type="theType"
					:name="name"
					:id="id"
					:disabled="disabled"
					@click="clicked"
					v-model="checkRadioValue"
					:true-value="true"
					:false-value="false"
					:ref="theRef" />

				<label
					:class="{ 'low-padding': lowLabelPadding }"
					:for="id"
					v-html="label"
					:style="labelStyles"
					v-if="label && ['checkbox', 'radio'].indexOf(theType) >= 0" />
			</div>

			<div v-if="['checkbox', 'radio'].indexOf(theType) >= 0">
				<div
					v-for="(item, itemIndex) in processedItems"
					:key="itemIndex"
					:class="[{ 'checkbox-wrap': true, inline: inline, tabbed: item.tabbed, disabled: item.disabled }, classes2]"
					:ref="theRef">
					<input
						:type="theType"
						:name="item.name"
						:id="item._id"
						:value="item.value"
						:disabled="item.disabled"
						:checked="item.checked"
						@click="checked(itemIndex, $event)" />

					<label
						:class="{ 'low-padding': lowLabelPadding }"
						:for="item._id"
						:style="[labelStyles, thisLabelStyles(item)]"
						v-html="item.label ? item.label : '&nbsp;'" />

					<slot></slot>

					<HelpTip v-if="item.tip" position="fromRight">{{ item.tip }}</HelpTip>
				</div>
			</div>
		</div>

		<p class="error single-input" v-if="hasError && !emptyError && !showAsInnerError" v-html="hasError" />
	</div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from "vue";
import passwordValidator from "password-validator";
import HelpTip from "./../HelpTip.vue";
import DateFilter from "./../Data/DateFilter.vue";
import general from "./../../utils/general";

export default {
	name: "FormInput",
	components: {
		HelpTip,
		DateFilter,
	},
	props: {
		aiAssistLabel: {
			type: String,
			default: "Write with AI",
		},
		type: {
			type: String,
			default: "text",
		},
		errors: [String, Object, Array],
		value: {
			type: [String, Array, Number, Date, Boolean],
			default: function () {
				return [];
			},
			//default: ''
		},
		modelValue: {
			type: [String, Array, Number, Date, Boolean],
			default: function () {
				return [];
			},
		},
		preName: {
			type: String,
			default: "",
		},
		forceLabel: {
			type: Boolean,
			default: false,
		},
		ignoreEmpty: {
			type: Boolean,
			default: false,
		},
		noMarginBottom: {
			type: Boolean,
			default: false,
		},
		name: String,
		placeholder: {
			type: String,
			default: "",
		},
		id: {
			type: String,
			default: "",
		},
		required: {
			type: Boolean,
			default: true,
		},
		inline: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		autocomplete: {
			type: String,
			default: "",
		},
		multiple: {
			type: Boolean,
			default: false,
		},
		label: String,
		lowLabelPadding: {
			type: Boolean,
			default: false,
		},
		min: {
			type: String,
			default: "",
		},
		max: {
			type: String,
			default: "",
		},
		items: {
			type: Array,
			default: function () {
				return [];
			},
		},
		tip: {
			type: String,
			default: "",
		},
		classes: {
			type: String,
			default: "",
		},
		labelStyles: {
			type: Object,
			default: function () {
				return {};
			},
		},
		theRef: {
			type: String,
			default: "",
		},
		focus: {
			type: Boolean,
			default: false,
		},
		autoResize: {
			type: Boolean,
			default: false,
		},
		autoResizeMaxHeight: {
			type: [Number, String],
			default: 0,
		},
		minHeight: {
			type: [Number, String],
			default: 0,
		},
		fontSize: {
			type: String,
			default: "",
		},
		fontSizeInput: {
			type: String,
			default: "",
		},
		triggerErrorCheck: {
			type: Number,
			default: 0,
		},
		timeoutErrorCheck: {
			type: Boolean,
			default: true,
		},
		requiredText: {
			type: String,
			default: "Required",
		},
		noLabel: {
			type: Boolean,
			default: false,
		},
		simplified: {
			type: Boolean,
			default: false,
		},
		aiAssist: {
			type: Boolean,
			default: false,
		},
		cornerLabel: {
			type: String,
			default: "",
		},
		detectEmails: {
			type: Boolean,
			default: false,
		},
		autoRemoveEmails: {
			type: Boolean,
			default: false,
		},
		backgroundColor: {
			type: String,
			default: "",
		},
		typeEffect: {
			type: Boolean,
			default: false,
		},
		typeEffectSpeed: {
			type: Number,
			default: 13,
		},
		typeEffectMaxLength: {
			type: Number,
			default: 1200,
		},
		typeEffectSkipKey: {
			type: [Number, String],
			default: 0,
		},
		textOnly: {
			type: Boolean,
			default: false,
		},
		textOnlyShowPlaceholder: {
			type: Boolean,
			default: true,
		},
		textOnlyLabel: {
			type: String,
			default: null,
		},
		textOnlyPlaceholder: {
			type: String,
			default: null,
		},
		singleLineSelectSelection: {
			type: Boolean,
			default: false,
		},
	},
	emits: [
		"input",
		"update:modelValue",
		"clicked",
		"aKeyup",
		"aKeyup-enter",
		"aKeyup-esc",
		"changed",
		"has-internal-errors",
		"keyup",
		"ai-assist",
		"email-found",
	],
	setup(props, { emit }) {
		const vm = getCurrentInstance().proxy;

		const internalError = ref(false);
		const emptyError = ref(false);
		const checkRadioValue = ref(props.value || props.modelValue);
		const hasFocus = ref(false);
		const showAsInnerError = ref(false);
		const displayValue = ref("");
		const isTypeEffectAnimating = ref(false);
		const lastConsumedTypeEffectSkipKey = ref(props.typeEffectSkipKey);
		let typeEffectTimeout = null;
		let typeEffectRunId = 0;
		let textareaVisibilityObserver = null;

		// Computed property for unified value handling (Vue 3 v-model compatibility)
		const currentValue = computed(() => {
			return props.modelValue !== undefined ? props.modelValue : props.value;
		});

		const emailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

		const handleEmailDetection = (value) => {
			if (!props.detectEmails || typeof value !== "string") return value;
			const matches = value.match(emailPattern) || [];
			const cleanedValue = props.autoRemoveEmails && matches.length > 0 ? value.replace(emailPattern, "") : value;
			emit("email-found", {
				emails: matches,
				found: matches.length > 0,
				value: value,
				cleanedValue: cleanedValue,
			});
			return cleanedValue;
		};

		const normaliseTextValue = (value) => {
			if (typeof value === "string") return value;
			if (value === null || typeof value === "undefined") return "";
			return String(value);
		};

		const clearTypeEffectTimeout = () => {
			if (!typeEffectTimeout) return;
			clearTimeout(typeEffectTimeout);
			typeEffectTimeout = null;
		};

		const cancelTypeEffect = () => {
			typeEffectRunId += 1;
			clearTypeEffectTimeout();
			isTypeEffectAnimating.value = false;
		};

		const waitForTypeEffect = (delay) => {
			clearTypeEffectTimeout();
			return new Promise((resolve) => {
				typeEffectTimeout = setTimeout(() => {
					typeEffectTimeout = null;
					resolve();
				}, delay);
			});
		};

		const resizeAfterValueChange = async () => {
			if (!(props.autoResize && theType.value === "textarea")) return;
			await nextTick();
			resizeTextarea();
		};

		const updateDisplayedValue = async (value) => {
			displayValue.value = normaliseTextValue(value);
			await resizeAfterValueChange();
		};

		const isWordBoundaryChar = (char) => {
			return /\s|[.,!?;:()[\]{}"'-]/.test(char);
		};

		const getTypeEffectDiff = (previousValue, nextValue) => {
			let start = 0;
			while (start < previousValue.length && start < nextValue.length && previousValue[start] === nextValue[start]) {
				start++;
			}

			let previousEnd = previousValue.length - 1;
			let nextEnd = nextValue.length - 1;

			while (previousEnd >= start && nextEnd >= start && previousValue[previousEnd] === nextValue[nextEnd]) {
				previousEnd--;
				nextEnd--;
			}

			const rawStart = start;

			while (start > 0) {
				const previousChar = previousValue[start - 1];
				const nextChar = nextValue[start - 1];
				if (previousChar !== nextChar) break;
				if (isWordBoundaryChar(previousChar) || isWordBoundaryChar(nextChar)) break;
				start--;
			}

			while (previousEnd < previousValue.length - 1 && nextEnd < nextValue.length - 1) {
				const previousChar = previousValue[previousEnd + 1];
				const nextChar = nextValue[nextEnd + 1];
				if (previousChar !== nextChar) break;
				if (isWordBoundaryChar(previousChar) || isWordBoundaryChar(nextChar)) break;
				previousEnd++;
				nextEnd++;
			}

			const suffixLength = previousValue.length - 1 - previousEnd;
			const changedMiddleLength = Math.max(
				previousValue.length - start - suffixLength,
				nextValue.length - start - suffixLength,
				rawStart === start ? 0 : 1
			);

			return {
				prefix: previousValue.slice(0, start),
				previousMiddle: previousValue.slice(start, previousValue.length - suffixLength),
				nextMiddle: nextValue.slice(start, nextValue.length - suffixLength),
				suffix: previousValue.slice(previousValue.length - suffixLength),
				changedMiddleLength,
			};
		};

		const runTypeEffect = async (previousValue, nextValue) => {
			const startValue = normaliseTextValue(previousValue);
			const finalValue = normaliseTextValue(nextValue);

			cancelTypeEffect();
			const runId = ++typeEffectRunId;
			isTypeEffectAnimating.value = true;

			if (props.theRef !== "" && vm.$refs[props.theRef] && typeof vm.$refs[props.theRef].blur === "function") {
				vm.$refs[props.theRef].blur();
			}

			const baseSpeed = Math.max(10, parseInt(props.typeEffectSpeed, 10) || 16);
			const { prefix, previousMiddle, nextMiddle, suffix, changedMiddleLength } = getTypeEffectDiff(startValue, finalValue);
			const speed = changedMiddleLength <= 24 ? Math.max(baseSpeed, 18) : baseSpeed;

			await updateDisplayedValue(startValue);

			for (let i = previousMiddle.length - 1; i >= 0; i--) {
				if (runId !== typeEffectRunId) return;
				await updateDisplayedValue(prefix + previousMiddle.slice(0, i) + suffix);
				await waitForTypeEffect(speed);
			}

			if (previousMiddle.length > 0 || nextMiddle.length > 0) {
				await waitForTypeEffect(Math.max(44, Math.round(speed * 1.2)));
			}

			for (let i = 1; i <= nextMiddle.length; i++) {
				if (runId !== typeEffectRunId) return;
				await updateDisplayedValue(prefix + nextMiddle.slice(0, i) + suffix);
				await waitForTypeEffect(speed);
			}

			if (runId === typeEffectRunId) {
				await updateDisplayedValue(finalValue);
				isTypeEffectAnimating.value = false;
			}
		};

		// Helper function to emit both input and update:modelValue for compatibility
		const emitValue = (newValue) => {
			const processedValue = handleEmailDetection(newValue);
			cancelTypeEffect();
			displayValue.value = normaliseTextValue(processedValue);
			if (props.autoResize && theType.value === "textarea") {
				setTimeout(resizeTextarea, 0);
			}
			emit("input", processedValue);
			emit("update:modelValue", processedValue);
		};

		const handleTextInput = (newValue) => {
			emitValue(newValue);
		};

		const theType = computed(() => {
			switch (props.type) {
				case "url":
				case "website":
					return "text";
				case "phone":
				case "telephone":
					return "tel";
			}
			return props.type;
		});

		const isMobile = computed(() => {
			return general.isMobile();
		});

		const isTypeEffectEligible = computed(() => props.typeEffect && theType.value === "textarea");

		const renderedValue = computed(() => {
			if (isTypeEffectEligible.value) {
				return displayValue.value;
			}
			return currentValue.value;
		});

		const resolvedLabel = computed(() => {
			if (props.textOnly && props.textOnlyLabel !== null) {
				return normaliseTextValue(props.textOnlyLabel);
			}
			return normaliseTextValue(props.label);
		});

		const showResolvedLabel = computed(() => resolvedLabel.value.length > 0 && !props.noLabel);

		const resolvedTextOnlyPlaceholder = computed(() => {
			if (props.textOnlyPlaceholder !== null) {
				return normaliseTextValue(props.textOnlyPlaceholder);
			}
			return normaliseTextValue(props.placeholder);
		});

		const textOnlyDisplayValue = computed(() => {
			const value = normaliseTextValue(renderedValue.value);
			if (value.length > 0) return value;
			if (!props.textOnlyShowPlaceholder) return "";
			return resolvedTextOnlyPlaceholder.value;
		});

		const isTextOnlyPlaceholder = computed(() => {
			return (
				props.textOnlyShowPlaceholder && normaliseTextValue(renderedValue.value).length === 0 && resolvedTextOnlyPlaceholder.value.length > 0
			);
		});

		const isInteractionBlocked = computed(() => isTypeEffectEligible.value && isTypeEffectAnimating.value);
		const showTypeEffectOverlay = computed(() => isInteractionBlocked.value);

		const innerError = computed(() => {
			if (emptyError.value) return "Required";
			if (showAsInnerError.value && internalError.value) return internalError.value;
			return false;
		});

		const textareaStyles = computed(() => {
			let styles = {};
			if (props.autoResize && !!props.autoResizeMaxHeight && isNaN(props.autoResizeMaxHeight)) {
				styles.maxHeight = props.autoResizeMaxHeight;
			}
			if (props.minHeight) {
				styles.minHeight = isNaN(props.minHeight) ? props.minHeight : props.minHeight + "px";
			}
			if (props.backgroundColor) {
				styles.backgroundColor = props.backgroundColor;
			}
			return styles;
		});

		const classes2 = computed(() => {
			let classes = {};
			if (props.classes !== "") {
				props.classes.split(" ").forEach(function (c) {
					classes[c] = true;
				});
			}
			if (props.fontSize) classes["font-size-" + props.fontSize] = true;
			if (props.fontSizeInput) classes["font-size-input-" + props.fontSizeInput] = true;
			if (props.simplified) classes["simplified"] = true;
			classes["no-margin"] = props.noMarginBottom;
			return classes;
		});

		const selectClasses = computed(() => {
			return {
				...classes2.value,
				"v-select--single-line-selection": props.singleLineSelectSelection,
			};
		});

		const processedItems = computed(() => {
			let items = [];
			props.items.forEach(function (item) {
				let processed = {};
				if (typeof item === "string") {
					let idBase = item.toLowerCase().trim().replace(/ /g, "-");
					let id = (props.preName ? props.preName + "-" : "") + idBase,
						name = theType.value === "radio" ? props.name || props.id || id : id;
					processed = {
						label: item,
						name: name,
						value: item,
						_id: id,
					};
				} else {
					processed = Object.assign({}, item);
					if (theType.value === "checkbox") {
						if (!processed.name) processed.name = processed._id;
					}
					if (theType.value === "radio") {
						if (!processed.name) processed.name = props.id || props.name;
					}
					processed.tabbed = !!item.tabbed;
					processed._id = (props.preName ? props.preName + "-" : "") + processed._id;
				}

				// Derive checked state purely from current value unless empty; use item.checked only as a fallback default
				if (theType.value === "checkbox" || theType.value === "radio") {
					const cv = currentValue.value;
					if (Array.isArray(cv)) {
						processed.checked = cv.indexOf(processed.value) >= 0;
					} else if (cv !== undefined && cv !== null && cv !== "") {
						processed.checked = cv === processed.value;
					} else {
						processed.checked = !!processed.checked; // preserve explicit default if provided on item
					}
				}

				items.push(processed);
			});
			return items;
		});

		const hasError = computed(() => {
			if (internalError.value || typeof props.errors === "undefined") {
				return internalError.value;
			}
			let err = [];
			if (typeof props.errors === "object" && Array.isArray(props.errors) === false && typeof props.errors[props.name] !== "undefined") {
				err = JSON.parse(JSON.stringify(props.errors[props.name]));
				err = typeof err.message !== "undefined" ? err.message : err;
			}
			if (Array.isArray(err) === false && err !== "") {
				err = [err];
			}
			if (err.length > 0) {
				return err.join(", ");
			}
			return false;
		});

		const clearTextareaVisibilityObserver = () => {
			if (!textareaVisibilityObserver) return;
			textareaVisibilityObserver.disconnect();
			textareaVisibilityObserver = null;
		};

		const isTextareaVisible = (textarea) => {
			if (!textarea) return false;
			return textarea.getClientRects().length > 0 && window.getComputedStyle(textarea).visibility !== "hidden";
		};

		const scheduleTextareaResizeWhenVisible = (textarea) => {
			if (!textarea || textareaVisibilityObserver) return;

			if (typeof IntersectionObserver === "undefined") {
				window.requestAnimationFrame(() => {
					if (isTextareaVisible(textarea)) {
						resizeTextarea();
					}
				});
				return;
			}

			textareaVisibilityObserver = new IntersectionObserver(
				(entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					clearTextareaVisibilityObserver();
					window.requestAnimationFrame(resizeTextarea);
				},
				{ threshold: 0 }
			);
			textareaVisibilityObserver.observe(textarea);
		};

		const resizeTextarea = () => {
			if (props.autoResize && theType.value === "textarea" && props.theRef !== "" && vm.$refs[props.theRef]) {
				const textarea = vm.$refs[props.theRef];
				if (!isTextareaVisible(textarea)) {
					scheduleTextareaResizeWhenVisible(textarea);
					return;
				}

				clearTextareaVisibilityObserver();
				const originalValue = textarea.value;
				if (!originalValue && props.placeholder) {
					textarea.value = props.placeholder;
				}
				textarea.style.height = "0px";
				const paddingTop = parseInt(window.getComputedStyle(textarea).paddingTop, 10);
				const paddingBottom = parseInt(window.getComputedStyle(textarea).paddingBottom, 10);
				const newHeight = textarea.scrollHeight - paddingTop - paddingBottom;
				const finalHeight =
					!!props.autoResizeMaxHeight && !isNaN(props.autoResizeMaxHeight)
						? Math.min(newHeight, parseInt(props.autoResizeMaxHeight, 10))
						: newHeight;
				let h = finalHeight + paddingTop + paddingBottom + (finalHeight > 100 ? Math.ceil(finalHeight / 20) : 0);
				if (h < 60) {
					h = 60;
				}
				textarea.style.height = `${h}px`;
				if (!originalValue && props.placeholder) {
					textarea.value = originalValue;
				}
			}
		};

		const errorCheck = () => {
			try {
				emit("keyup");
				internalError.value = false;
				showAsInnerError.value = false;
				emptyError.value = false;
				const UrlPattern = require("url-pattern");
				const pattern = new UrlPattern("http(s)\\://(*.):domain.:tld(\\::port)(/*)");
				let match,
					empty = false,
					vals = general.stripObject(currentValue.value);
				if (props.required && (!vals || (Array.isArray(vals) && vals.length === 0))) {
					if (!props.ignoreEmpty && !hasFocus.value) {
						internalError.value = "This field is required";
						emptyError.value = true;
					}
				} else if (vals) {
					switch (props.type) {
						case "email":
							if (hasFocus.value) break;
							if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,20})+$/.test(vals) === false) {
								internalError.value = "Invalid email";
								showAsInnerError.value = true;
							}
							break;
						case "url":
							match = pattern.match(vals);
							if (match === null) {
								internalError.value = "Invalid URL";
								if (vals.length > 3) {
									if (vals.length > 7 && vals.substring(0, 8) !== "https://") {
										internalError.value = "Must start with https://";
									} else {
										let c = vals.split("://");
										if ((c[0].toLowerCase() === "http" || c[0].toLowerCase() === "https") && c[0].toLowerCase() !== c[0]) {
											internalError.value = c[0] + " must be lowercase";
										}
									}
								}
								showAsInnerError.value = true;
							}
							break;
						case "password":
							if (hasFocus.value) break;
							if (vals.length > 0 && vals.length < 8) {
								internalError.value = "Your password be at least 8 characters";
							} else {
								var passwordSchema = new passwordValidator();
								passwordSchema
									.is()
									.min(8)
									.is()
									.max(30)
									.has()
									.uppercase()
									.has()
									.lowercase()
									.has()
									.digits()
									.has()
									.not()
									.spaces()
									.is()
									.not()
									.oneOf(["Passw0rd", "Password123"]);
								var pwError = passwordSchema.validate(vals, {
										list: true,
									}),
									pwErrorList = [];
								if (pwError.length > 0) {
									var pwErrorMessages = {
										min: "be at least 8 characters",
										max: "be at most 30 characters",
										digits: "contain a number",
										uppercase: "have an uppercase character",
										lowercase: "have an lowercase character",
										spaces: "not contain spaces",
										oneOf: " be harder",
									};
									pwError.forEach(function (e) {
										if (typeof pwErrorMessages[e] !== "undefined") {
											pwErrorList.push(pwErrorMessages[e]);
										} else {
											console.log("missing ", e);
										}
									});
									internalError.value = "Your password must " + pwErrorList.join(", ");
								}
							}
							break;
					}
				}

				if (internalError.value && props.timeoutErrorCheck) {
					setTimeout(() => {
						errorCheck();
					}, 500);
				}

				if (props.required) {
					emit("has-internal-errors", {
						error: internalError.value,
						name: props.name,
						empty: empty,
					});
				}
				return true;
			} catch (err) {
				console.log("err", err);
			}
		};

		watch(checkRadioValue, (newVal, oldVal) => {
			if (newVal !== oldVal) emitValue(newVal);
		});

		watch(
			() => props.triggerErrorCheck,
			(newVal, oldVal) => {
				if (newVal !== oldVal) errorCheck();
			}
		);

		watch(
			() => currentValue.value,
			async (newVal, oldVal) => {
				if (["checkbox", "radio"].indexOf(theType.value) >= 0) {
					checkRadioValue.value = newVal;
				}

				const nextTextValue = normaliseTextValue(newVal);
				const currentDisplay = normaliseTextValue(displayValue.value);
				const previousTextValue = normaliseTextValue(oldVal);
				const hasExistingContent = currentDisplay.length > 0;
				const changedLength = Math.abs(nextTextValue.length - currentDisplay.length);
				const combinedLength = currentDisplay.length + nextTextValue.length;
				const shouldSkipTypeEffect = props.typeEffectSkipKey !== lastConsumedTypeEffectSkipKey.value;
				const shouldAnimate =
					isTypeEffectEligible.value &&
					!shouldSkipTypeEffect &&
					!props.disabled &&
					hasExistingContent &&
					nextTextValue !== currentDisplay &&
					combinedLength <= props.typeEffectMaxLength &&
					changedLength <= props.typeEffectMaxLength;

				if (shouldSkipTypeEffect && nextTextValue !== currentDisplay) {
					lastConsumedTypeEffectSkipKey.value = props.typeEffectSkipKey;
				}

				if (shouldAnimate) {
					await runTypeEffect(currentDisplay || previousTextValue, nextTextValue);
					return;
				}

				cancelTypeEffect();
				displayValue.value = nextTextValue;

				if (props.autoResize && theType.value === "textarea") {
					setTimeout(resizeTextarea, 0);
				}
			}
		);

		watch(
			() => props.placeholder,
			async (newVal, oldVal) => {
				if (newVal === oldVal || !(props.autoResize && theType.value === "textarea")) return;
				await nextTick();
				resizeTextarea();
			}
		);

		watch(
			() => props.textOnly,
			async (nextTextOnly, prevTextOnly) => {
				if (nextTextOnly || nextTextOnly === prevTextOnly) return;
				await nextTick();
				resizeTextarea();
			}
		);

		const thisLabelStyles = (item) => {
			return item.styles || {};
		};

		const clicked = () => {
			emit("clicked");
		};

		const emitIt = (type, ev) => {
			emit(type, ev);
		};

		const emitKeyUp = (type) => {
			emit("aKeyup-" + type);
		};

		const keyuped = ($event) => {
			errorCheck();
			resizeTextarea();
			emitIt("aKeyup", $event);
		};

		const blurred = () => {
			hasFocus.value = false;
			errorCheck();
			resizeTextarea();
		};

		const focussed = () => {
			hasFocus.value = true;
			resizeTextarea();
		};

		const setDate = (info) => {
			emitValue(info);
		};

		const checked = (itemIndex, event) => {
			let values = Array.isArray(currentValue.value) ? [...currentValue.value] : currentValue.value;
			let index;
			if (theType.value === "checkbox") {
				index = values.indexOf(processedItems.value[itemIndex].value);
				if (event.target.checked) {
					if (index < 0) {
						values.push(processedItems.value[itemIndex].value);
					}
				} else if (index !== -1) {
					values.splice(index, 1);
				}
				emitValue(values);
			} else {
				emitValue(processedItems.value[itemIndex].value);
			}
			emit("changed", processedItems.value[itemIndex]);

			setTimeout(() => {
				errorCheck();
			}, 100);
		};

		const handleVSelectInput = (val) => {
			emitValue(val);
			errorCheck();
		};

		onMounted(() => {
			displayValue.value = normaliseTextValue(currentValue.value);

			if (props.theRef !== "") {
				setTimeout(() => {
					if (vm.$refs[props.theRef]) {
						if (props.focus) vm.$refs[props.theRef].focus();
						resizeTextarea();
					}
				}, 100);

				if (props.autoResize && theType.value === "textarea") {
					setTimeout(() => {
						const el = vm.$refs[props.theRef];
						if (!el) return;
						scheduleTextareaResizeWhenVisible(el);
					}, 50);
				}
			}
		});

		onBeforeUnmount(() => {
			cancelTypeEffect();
			clearTextareaVisibilityObserver();
			emit("has-internal-errors", {
				error: false,
				name: props.name,
			});
		});

		return {
			internalError,
			emptyError,
			checkRadioValue,
			hasFocus,
			showAsInnerError,
			isMobile,
			theType,
			innerError,
			classes2,
			selectClasses,
			processedItems,
			hasError,
			currentValue,
			renderedValue,
			resolvedLabel,
			showResolvedLabel,
			textOnlyDisplayValue,
			isTextOnlyPlaceholder,
			emitValue,
			handleTextInput,
			thisLabelStyles,
			clicked,
			keyuped,
			blurred,
			focussed,
			resizeTextarea,
			emitIt,
			emitKeyUp,
			setDate,
			checked,
			errorCheck,
			handleVSelectInput,
			textareaStyles,
			isInteractionBlocked,
			isTypeEffectAnimating,
			showTypeEffectOverlay,
		};
	},
};
</script>
<style lang="less" scoped="true">
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";

.label-wrap {
	margin-bottom: 5px;
	> label {
		sup {
			color: @formDefaultColour;
			font-size: 0.8em;
		}
	}
}

.type-effect-shell {
	position: relative;
}

.type-effect-overlay {
	position: absolute;
	inset: 0;
	z-index: 2;
	background: transparent;
	cursor: progress;
}

[type="text"],
[type="password"],
[type="submit"],
[type="email"],
[type="number"],
[type="search"],
textarea {
	border: 1px solid fade(@guidedRose, 10%);
	background-color: fade(@guidedRose, 5%);
	.rounded(@radiusMd);
	margin-bottom: 30px;
	text-align: left;
	padding: 10px 18px;
	outline: none;
	width: 100%;
	font-size: 16px;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	display: block;
	&.font-size-input-18 {
		font-size: 18px;
	}
	&.font-size-input-20 {
		font-size: 20px;
	}
	&.font-weight-400 {
		font-weight: 400;
	}

	&.has-error {
		margin-bottom: 5px;
	}

	&.has-tip {
		padding-right: 34px;
	}

	&.no-margin {
		margin-bottom: 0;
	}

	&::placeholder {
		color: @textSubtle;
	}
}

textarea {
	line-height: 1.5em;
	&.simplified {
		background: none;
		border: none;
		padding: 0;
		margin: 0;
		resize: none;
		font-size: 20px;
	}
}

.form-input-text-only {
	margin-bottom: 30px;
	text-align: left;
	outline: none;
	width: 100%;
	font-size: 16px;
	display: block;
	color: inherit;
	font-family: inherit;
	background: none;
	border: none;
	padding: 0;
	min-height: 1.5em;

	&.font-size-input-18 {
		font-size: 18px;
	}

	&.font-size-input-20 {
		font-size: 20px;
	}

	&.font-weight-400 {
		font-weight: 400;
	}

	&.has-error {
		margin-bottom: 5px;
	}

	&.no-margin {
		margin-bottom: 0;
	}

	&.form-input-text-only--multiline {
		white-space: pre-wrap;
		line-height: 1.5em;
	}

	&.form-input-text-only--placeholder {
		color: @textSubtle;
	}
}

[type="search"] {
	height: 35px;
}

[type="search"] {
	background: @surfaceInputActive
		url("data:image/svg+xml;utf8,<svg width='18' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M12.118 13.44a6.078 6.078 0 111.322-1.322l.017.017 1.815 1.815a.935.935 0 11-1.323 1.322l-1.814-1.814-.017-.017zm.756-4.907a4.341 4.341 0 11-8.683 0 4.341 4.341 0 018.683 0z' fill='@{darkContrastNoHash}'/><mask id='a' maskUnits='userSpaceOnUse' x='2' y='2' width='14' height='14'><path fill-rule='evenodd' clip-rule='evenodd' d='M12.118 13.44a6.078 6.078 0 111.322-1.322l.017.017 1.815 1.815a.935.935 0 11-1.323 1.322l-1.814-1.814-.017-.017zm.756-4.907a4.341 4.341 0 11-8.683 0 4.341 4.341 0 018.683 0z' fill='@{darkContrastNoHash}'/></mask><g mask='url(%23a)'><path fill='%238697A8' d='M0 0h18v18H0z'/></g></svg>")
		10px center no-repeat;
	padding-left: 34px;
}

[type="checkbox"],
[type="radio"] {
	&:not(old) {
		width: 28px;
		margin: 0;
		padding: 0;
		opacity: 0;
		position: absolute;

		& + label {
			display: inline-block;
			margin-left: 0;
			padding-left: 24px;
			line-height: 24px;
			position: relative;
			&:before {
				width: 18px;
				height: 18px;
				content: "";
				position: absolute;
				left: 0;
				top: 3px;
				background: url("data:image/svg+xml;utf8,<svg width='138' height='64' fill='none' xmlns='http://www.w3.org/2000/svg'><mask id='a' maskUnits='userSpaceOnUse' x='1' y='48' width='16' height='16'><rect x='1' y='48' width='16' height='16' rx='4' fill='%23C4C4C4'/></mask><g mask='url(%23a)'><path fill='@{formDefaultColourNoHash}' d='M1 48h16v16H1z'/></g><g filter='url(%23filter0_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M13.792 52.236a.758.758 0 01-.028 1.07l-6.06 5.758a.758.758 0 01-1.058-.013l-2.424-2.424a.758.758 0 011.071-1.072l1.902 1.902 5.526-5.249a.758.758 0 011.07.028z' fill='%23fff'/></g><path d='M133.381 4.299a.75.75 0 00-1.265.807l1.265-.807zm-3.479-1.41a.75.75 0 00.809-1.264l-.809 1.264zM133.25 9a7.25 7.25 0 01-7.25 7.25v1.5A8.75 8.75 0 00134.75 9h-1.5zM126 16.25A7.25 7.25 0 01118.75 9h-1.5a8.75 8.75 0 008.75 8.75v-1.5zM118.75 9A7.25 7.25 0 01126 1.75V.25A8.75 8.75 0 00117.25 9h1.5zm13.366-3.894A7.21 7.21 0 01133.25 9h1.5a8.71 8.71 0 00-1.369-4.701l-1.265.807zM126 1.75c1.438 0 2.776.418 3.902 1.139l.809-1.264A8.712 8.712 0 00126 .25v1.5z' fill='@{formDefaultColourNoHash}'/><g filter='url(%23filter1_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M135.027 2.429A.757.757 0 01135 3.5l-9.296 8.565a.758.758 0 01-1.058-.014l-2.424-2.424a.757.757 0 111.071-1.072l1.902 1.902 8.761-8.055a.758.758 0 011.071.027z' fill='%23B3C0CE'/></g><circle cx='68' cy='9' r='8' stroke='@{formDefaultColourNoHash}' stroke-width='1.5'/><path d='M16.38 4.299a.75.75 0 10-1.263.807l1.264-.807zm-3.478-1.41a.75.75 0 10.809-1.264l-.809 1.264zM16.25 9A7.25 7.25 0 019 16.25v1.5A8.75 8.75 0 0017.75 9h-1.5zM9 16.25A7.25 7.25 0 011.75 9H.25A8.75 8.75 0 009 17.75v-1.5zM1.75 9A7.25 7.25 0 019 1.75V.25A8.75 8.75 0 00.25 9h1.5zm13.367-3.894A7.212 7.212 0 0116.25 9h1.5a8.712 8.712 0 00-1.37-4.701l-1.263.807zM9 1.75c1.438 0 2.776.418 3.902 1.139l.809-1.264A8.713 8.713 0 009 .25v1.5z' fill='@{formDefaultColourNoHash}'/><g filter='url(%23filter2_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M18.027 2.429A.758.758 0 0118 3.5l-9.296 8.565a.758.758 0 01-1.058-.014L5.222 9.627a.758.758 0 011.071-1.072l1.902 1.902 8.761-8.055a.758.758 0 011.072.027z' fill='@{formDefaultColourNoHash}'/></g><mask id='b' maskUnits='userSpaceOnUse' x='57' y='48' width='16' height='16'><rect x='57' y='48' width='16' height='16' rx='4' fill='%23C4C4C4'/></mask><g mask='url(%23b)'><path fill='%23F8FAFB' d='M57 48h16v16H57z'/></g><mask id='c' maskUnits='userSpaceOnUse' x='57' y='48' width='16' height='16'><rect x='57.5' y='48.5' width='15' height='15' rx='3.5' stroke='%23F8FAFB'/></mask><g mask='url(%23c)'><path fill='@{formDefaultColourNoHash}' d='M57 48h16v16H57z'/></g><mask id='d' maskUnits='userSpaceOnUse' x='113' y='48' width='16' height='16'><rect x='113' y='48' width='16' height='16' rx='4' fill='%23C4C4C4'/></mask><g mask='url(%23d)'><path fill='%23F8FAFB' d='M113 48h16v16h-16z'/></g><mask id='e' maskUnits='userSpaceOnUse' x='113' y='48' width='16' height='16'><rect x='113.5' y='48.5' width='15' height='15' rx='3.5' stroke='%23F8FAFB'/></mask><g mask='url(%23e)'><path fill='@{formDefaultColourNoHash}' d='M113 48h16v16h-16z'/></g><g filter='url(%23filter3_d)'><path fill-rule='evenodd' clip-rule='evenodd' d='M125.792 52.236a.757.757 0 01-.028 1.07l-6.06 5.758a.758.758 0 01-1.058-.013l-2.424-2.424a.757.757 0 111.071-1.072l1.902 1.902 5.526-5.249a.758.758 0 011.071.028z' fill='%23B3C0CE'/></g><defs><filter id='filter0_d' x='2' y='51' width='14' height='11.273' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.5 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter><filter id='filter1_d' x='120' y='1.193' width='17.236' height='14.08' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.25 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter><filter id='filter2_d' x='3' y='1.193' width='17.236' height='14.08' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.25 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter><filter id='filter3_d' x='114' y='51' width='14' height='11.273' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/><feOffset dy='1'/><feGaussianBlur stdDeviation='1'/><feColorMatrix values='0 0 0 0 0.266667 0 0 0 0 0.337255 0 0 0 0 0.423529 0 0 0 0.25 0'/><feBlend in2='BackgroundImageFix' result='effect1_dropShadow'/><feBlend in='SourceGraphic' in2='effect1_dropShadow' result='shape'/></filter></defs></svg>")
					0 0 no-repeat;
			}
		}
	}
}

[type="radio"] {
	&:not(old) {
		& + label {
			&:before {
				background-position: -59px 0;
			}
		}
		&:checked {
			& + label {
				&:before {
					background-position: 0 0;
				}
			}
		}
	}
}
[type="checkbox"] {
	&:not(old) {
		& + label {
			&:before {
				background-position: -57px -47px;
			}
		}
		&:checked {
			& + label {
				&:before {
					background-position: 0 -47px;
				}
			}
		}
	}
}

.checkbox-wrap {
	text-align: left;
	margin-top: 4px;
	position: relative;

	.tip {
		position: relative !important;
		top: auto !important;
		right: auto !important;
	}

	&.tabbed {
		padding-left: 26px;
	}

	label {
		display: inline;
		vertical-align: top;
		padding-top: 0px;
		padding-bottom: 10px;
		font-size: 16px;
		-webkit-touch-callout: none; /* iOS Safari */
		-webkit-user-select: none; /* Safari */
		-khtml-user-select: none; /* Konqueror HTML */
		-moz-user-select: none; /* Old versions of Firefox */
		-ms-user-select: none; /* Internet Explorer/Edge */
		user-select: none;
	}

	&.disabled {
		label {
			.opacity(0.6);
		}
	}

	&.inline {
		display: inline-block;
		margin: 0 20px 0 0;

		&:last-child {
			margin-right: 0;
		}
	}

	&.font-size-14 {
		label {
			font-size: 14px;
		}
	}
}

.form-input-wrap {
	margin-bottom: 0;
	&.no-margin-bottom {
		label {
			padding-bottom: 0;
		}
	}
	&.no-margin {
		[type="text"],
		[type="password"],
		[type="submit"],
		[type="email"],
		[type="number"],
		[type="search"],
		textarea {
			margin-bottom: 0;
		}
	}
	&.inline {
		display: inline;
	}
	&.inline-block {
		display: inline-block;
	}
	&.checkbox-label-line-split {
		.checkbox-wrap {
			label {
				line-height: 1.15em;
			}
		}
	}
}

label {
	font-size: 14px;
	display: block;
	font-weight: normal;
	padding-bottom: 8px;
	.opacity(0.8);

	&.error {
		color: @red;
	}

	&.low-padding {
		padding-bottom: 4px;
	}
}

.date-filter-wrap,
.v-select {
	margin-bottom: 30px;
	&.has-error {
		margin-bottom: 5px;
	}
}

textarea {
	height: 140px;

	&.two-row {
		height: 60px;
	}

	&.auto-resize {
		height: auto;
	}
}

[type="submit"] {
	background-color: @formDefaultColour;
	color: #fff;
	padding: 10px;
	margin: 0 auto 14px;
	width: 100%;

	&:hover {
		background-color: #000;
	}
}
.input-wrap-wrap {
	position: relative;
	&.check {
		position: static;
		display: inline;
		label {
			display: inline-block;
		}
	}
	&.inner-error {
		position: relative;
		.vertical-align-parent;
		.inner-error {
			font-size: 10px;
			display: block;
			position: absolute;
			right: 8px;
			background-color: @formDefaultColour;
			color: #fff;
			.rounded(@radiusMd);
			padding: 3px 6px;
			right: 10px;
			.vertical-align;
		}
	}
	.tip {
		position: absolute;
		right: 0;
		top: 0px;
	}
}
.input-corner-label {
	position: absolute;
	top: 0;
	right: 0;
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: @guidedRose;
	background: @guidedRoseBgStrong;
	border: 1px solid fade(@guidedRose, 15%);
	padding: 0px 6px;
	border-radius: 0 @radiusSm 0 @radiusLg;
	z-index: 2;
}

@media (max-width: 420px) {
	textarea {
	}
}
</style>
